import { useEffect, useRef, useState } from "react";
import { PencilSimple } from "@phosphor-icons/react";
import { applyMask } from "shared/utils/masks";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "components/ui/tooltip";

export interface CurrencyComparison {
  /** Benchmark value to compare against (typically ERV or ERV provisies). */
  benchmark: number | null | undefined;
  /** Human label for the benchmark, used in the tooltip body. */
  benchmarkLabel?: string;
  /** Human label for the compared value, used in the tooltip body. */
  valueLabel?: string;
}

export interface EditableCurrencyCellProps {
  value: number | null | undefined;
  /** Called with the new numeric value (or null when the field is cleared). */
  onSave: (value: number | null) => Promise<void> | void;
  /** Placeholder shown when the value is empty and not editing. */
  emptyLabel?: string;
  /** Aria label / title on the pencil for screen readers. */
  editLabel?: string;
  /** Optional benchmark to render a green/orange indicator next to the value. */
  comparison?: CurrencyComparison;
}

/**
 * Tight inline currency editor used inside leegstand-style tables.
 * - Value renders with the shared currency mask when not editing.
 * - Pencil opens a number input; Opslaan / Enter persists, Escape / Annuleren
 *   cancels. Clearing the input and saving sends `null` so columns can be
 *   emptied again.
 */
export const EditableCurrencyCell = ({
  value,
  onSave,
  emptyLabel = "—",
  editLabel = "Bewerken",
  comparison,
}: EditableCurrencyCellProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<string>(
    value === null || value === undefined ? "" : String(value),
  );
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isEditing) {
      setDraft(value === null || value === undefined ? "" : String(value));
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }
  }, [isEditing, value]);

  const handleSave = async () => {
    const trimmed = draft.trim();
    let next: number | null;
    if (trimmed === "") {
      next = null;
    } else {
      const parsed = Number(trimmed.replace(",", "."));
      if (!Number.isFinite(parsed)) return;
      next = parsed;
    }
    setIsSaving(true);
    try {
      await onSave(next);
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-1">
        <div className="relative">
          <span className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-xs text-neutral-secondary-mid">
            €
          </span>
          <input
            ref={inputRef}
            type="number"
            step="any"
            min={0}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSave();
              }
              if (e.key === "Escape") setIsEditing(false);
            }}
            disabled={isSaving}
            className="w-24 rounded-sm border border-neutral-primary/30 pl-5 pr-1 py-1 text-xs focus:border-moss-primary focus:outline-hidden"
          />
        </div>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="rounded-sm bg-moss-primary px-2 py-1 text-xs text-white transition-colors hover:bg-moss-primary/90 disabled:opacity-50"
        >
          {isSaving ? "…" : "✓"}
        </button>
        <button
          onClick={() => setIsEditing(false)}
          disabled={isSaving}
          className="rounded-sm border border-neutral-primary/20 px-2 py-1 text-xs text-neutral-primary hover:bg-neutral-primary/5"
        >
          ✕
        </button>
      </div>
    );
  }

  const display =
    value === null || value === undefined ? (
      <span className="text-sm text-neutral-secondary-mid">{emptyLabel}</span>
    ) : (
      <span className="text-sm text-neutral-primary">
        {applyMask(value, "valuta")}
      </span>
    );

  return (
    <div className="group flex items-center justify-between gap-1">
      <div className="flex items-center gap-1.5">
        {display}
        {typeof value === "number" && comparison ? (
          <ComparisonIndicator value={value} {...comparison} />
        ) : null}
      </div>
      <button
        onClick={() => setIsEditing(true)}
        className="rounded-sm p-1 opacity-0 transition-opacity hover:bg-neutral-primary/5 group-hover:opacity-100"
        title={editLabel}
        aria-label={editLabel}
      >
        <PencilSimple size={14} />
      </button>
    </div>
  );
};

interface ComparisonIndicatorProps extends CurrencyComparison {
  value: number;
}

const ComparisonIndicator = ({
  value,
  benchmark,
  benchmarkLabel = "ERV",
  valueLabel = "Geafficheerde waarde",
}: ComparisonIndicatorProps) => {
  if (benchmark === null || benchmark === undefined || Number.isNaN(benchmark)) {
    return null;
  }

  // Treat within €1 as "on ERV" so rounding doesn't flip the colour back and
  // forth for tiny deltas.
  const diff = value - benchmark;
  if (Math.abs(diff) < 1) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              aria-label={`${valueLabel} gelijk aan ${benchmarkLabel}`}
              className="inline-block h-2 w-2 rounded-full bg-neutral-primary/40"
            />
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <div className="space-y-0.5 text-xs">
              <p className="font-semibold">Gelijk aan {benchmarkLabel}</p>
              <p className="text-neutral-secondary-mid">
                {valueLabel} {applyMask(value, "valuta")} ≈ {benchmarkLabel} {applyMask(benchmark, "valuta")}
              </p>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const isAbove = diff > 0;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            aria-label={
              isAbove
                ? `${valueLabel} boven ${benchmarkLabel}`
                : `${valueLabel} onder ${benchmarkLabel}`
            }
            className={`inline-block h-2 w-2 rounded-full ${
              isAbove ? "bg-green-500" : "bg-amber-500"
            }`}
          />
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <div className="space-y-0.5 text-xs">
            <p className="font-semibold">
              {isAbove
                ? `Boven ${benchmarkLabel}`
                : `Onder ${benchmarkLabel}`}
            </p>
            <p>
              {valueLabel}: {applyMask(value, "valuta")} · {benchmarkLabel}:{" "}
              {applyMask(benchmark, "valuta")}
            </p>
            <p className="text-neutral-secondary-mid">
              {isAbove
                ? `We vragen ${applyMask(Math.abs(diff), "valuta")} meer dan onze interne schatting.`
                : `We vragen ${applyMask(Math.abs(diff), "valuta")} minder dan onze interne schatting.`}
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default EditableCurrencyCell;
